import { useEffect, useState } from 'react';
import type { MatchView, Seat } from '@sf72/shared';

/**
 * The two players and the clock.
 *
 * The countdown is driven locally from `turnDeadline` and corrected by the
 * server-clock offset the socket measures, so it stays honest without a message
 * per second.
 */

function Avatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
  if (photoUrl) {
    return <img src={photoUrl} alt="" className="h-9 w-9 rounded-full object-cover" />;
  }
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-sm font-semibold text-accent">
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

function useCountdown(deadline: number | null, skewMs: number): number | null {
  const [seconds, setSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (deadline === null) {
      setSeconds(null);
      return;
    }

    const tick = (): void => {
      const now = Date.now() + skewMs;
      setSeconds(Math.max(0, Math.round((deadline - now) / 1000)));
    };

    tick();
    const handle = window.setInterval(tick, 500);
    return () => window.clearInterval(handle);
  }, [deadline, skewMs]);

  return seconds;
}

export function PlayerBar({
  match,
  yourSeat,
  skewMs,
}: {
  match: MatchView;
  yourSeat: Seat | null;
  skewMs: number;
}) {
  const secondsLeft = useCountdown(match.turnDeadline, skewMs);
  const running = match.status === 'active';

  /**
   * Five of the boards here are played by both players at once, and in those
   * nobody is "on turn" — so a clock drawn beside whoever is to move was drawn
   * nowhere at all. The round still ran out; it just did so without warning.
   */
  const sharedClock = running && match.turnSeat === null && secondsLeft !== null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-stretch gap-2">
        {match.players.map((player) => {
          const onTurn = running && match.turnSeat === player.seat;
          const isYou = player.seat === yourSeat;
          // Below ten seconds the number turns red — the only place colour is
          // used to carry meaning rather than decoration.
          const urgent = onTurn && secondsLeft !== null && secondsLeft <= 10;

          return (
            <div
              key={player.seat}
              className={`flex flex-1 items-center gap-2 rounded-2xl px-3 py-2 transition ${
                onTurn ? 'bg-accent/12 ring-1 ring-accent/40' : 'bg-surface'
              }`}
            >
              <div className="relative">
                <Avatar name={player.user.firstName} photoUrl={player.user.photoUrl} />
                <span
                  className={`absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full border-2 border-bg ${
                    player.online ? 'bg-emerald-500' : 'bg-hint'
                  }`}
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-medium">
                  {isYou ? 'Siz' : player.user.firstName}
                </div>
                <div className="text-[12px] text-hint">{player.rating}</div>
              </div>

              {onTurn && secondsLeft !== null && (
                <div
                  className={`tabular-nums text-[15px] font-semibold ${
                    urgent ? 'text-danger' : 'text-text'
                  }`}
                >
                  {secondsLeft}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sharedClock && (
        <div
          className={`self-center rounded-full bg-surface px-3 py-1 text-[13px] font-semibold tabular-nums ${
            secondsLeft <= 10 ? 'text-danger' : 'text-hint'
          }`}
        >
          ⏳ {secondsLeft}
        </div>
      )}
    </div>
  );
}
